import { Link } from 'react-router-dom';
import { Flame, Snowflake, Phone, CalendarCheck, Wrench } from 'lucide-react';
import { getCurrentSeason } from './SeasonBadge';

export const TUNE_UP_SERVICE = 'Furnace / Heating Tune-Up';
export const WINTER_SERVICE = 'Frozen Pipe / No-Heat Service';

const BANNERS = {
  tuneUp: {
    testId: 'tune-up',
    TagIcon: Flame,
    CtaIcon: CalendarCheck,
    accent: 'bg-[#F58220]',
    service: TUNE_UP_SERVICE,
    cta: 'Book a Tune-Up',
  },
  winter: {
    testId: 'winter-service',
    TagIcon: Snowflake,
    CtaIcon: Wrench,
    accent: 'bg-sky-500',
    service: WINTER_SERVICE,
    cta: 'Request Service',
    tag: 'Winter Service',
    title: 'Frozen Pipes or No Heat?',
    text: 'When a Minnesota cold snap leaves you with frozen pipes or a furnace or boiler that won\u2019t keep up, our licensed technicians are ready to help get your home back to normal. Call us or request a visit online.',
  },
};

const tuneUpCopy = () => {
  const isFall = getCurrentSeason() === 'fall';
  return {
    tag: isFall ? 'Fall Tune-Up Season' : 'Heating Season',
    title: isFall ? 'Schedule Your Fall Furnace Tune-Up' : 'Schedule Your Furnace Tune-Up',
    text: `Get your heating system checked by our licensed technicians before the next Minnesota cold snap. ${isFall ? 'Book now while fall appointments are open.' : 'Book a visit to keep your system running safely and efficiently.'}`,
  };
};

const ServiceBanner = ({ variant, className }) => {
  const cfg = { ...BANNERS[variant], ...(variant === 'tuneUp' ? tuneUpCopy() : {}) };
  const { TagIcon, CtaIcon } = cfg;
  return (
    <aside
      className={`${className} relative overflow-hidden rounded-xl bg-[#14366C] p-8 md:p-10`}
      data-testid={`${cfg.testId}-banner`}
    >
      <div className={`absolute -right-10 -top-10 h-40 w-40 rounded-full ${cfg.accent} opacity-20`} aria-hidden="true" />
      <div className={`absolute -right-4 bottom-0 h-24 w-24 rounded-full ${cfg.accent} opacity-10`} aria-hidden="true" />
      <div className="relative">
        <span className={`inline-flex items-center gap-1.5 rounded-full ${cfg.accent} px-3 py-1 text-xs font-bold uppercase tracking-wider text-white`}>
          <TagIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {cfg.tag}
        </span>
        <h3 className="mt-4 font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-testid={`${cfg.testId}-banner-title`}>
          {cfg.title}
        </h3>
        <p className="mt-3 max-w-xl text-gray-300">{cfg.text}</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link
            to={`/contact?service=${encodeURIComponent(cfg.service)}`}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#F58220] px-7 py-3 font-oswald font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#e07010]"
            data-testid={`${cfg.testId}-book-btn`}
          >
            <CtaIcon className="h-5 w-5" aria-hidden="true" />
            {cfg.cta}
          </Link>
          <a
            href="tel:651-738-9354"
            className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-white px-7 py-3 font-oswald font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-[#14366C]"
            data-testid={`${cfg.testId}-call-btn`}
          >
            <Phone className="h-5 w-5" aria-hidden="true" />
            Call 651-738-9354
          </a>
        </div>
      </div>
    </aside>
  );
};

export const TuneUpBanner = ({ className = 'mt-12' }) => <ServiceBanner variant="tuneUp" className={className} />;

export const WinterServiceBanner = ({ className = 'mt-12' }) => <ServiceBanner variant="winter" className={className} />;
